import axios from "axios"

export interface EmailAttachment {
  filename: string
  content: string
  encoding?: string
  contentType?: string
}

export interface EmailResponse {
  success: boolean
  message: string
  messageId?: string
  error?: string
}

export default class MailApi {
  private baseUrl: string

  constructor() {
    this.baseUrl = "https://us-central1-nkurenkuru-town-council.cloudfunctions.net"
  }

  // Basic email sending
  async sendMail(to: string[], from: string, cc: string[], subject: string, message: string): Promise<EmailResponse> {
    try {
      const response = await axios.post(`${this.baseUrl}/sendMail`, {
        to,
        from,
        cc,
        subject,
        message,
      })
      return response.data
    } catch (error) {
      console.error("Error sending email:", error)
      throw new Error(`Failed to send email: ${error}`)
    }
  }

  // Email with upload/attachment support
  async sendMailUpload(
    to: string[],
    from: string,
    cc: string[],
    subject: string,
    message: string,
    ticketNumber: string,
    attachments?: EmailAttachment[],
  ): Promise<EmailResponse> {
    try {
      const response = await axios.post(`${this.baseUrl}/sendMailUpload`, {
        to,
        from,
        cc,
        subject,
        message,
        ticketNumber,
        attachments,
      })
      return response.data
    } catch (error) {
      console.error("Error sending email with upload:", error)
      throw new Error(`Failed to send email with upload: ${error}`)
    }
  }

  // Scorecard email with PDF attachment
  async scorecardMail(
    to: string,
    from: string,
    subject: string,
    message: string,
    pdfContent?: string,
    htmlContent?: string,
    filename?: string,
  ): Promise<EmailResponse> {
    try {
      const response = await axios.post(`${this.baseUrl}/sendScorecardEmail`, {
        to,
        from,
        subject,
        message,
        pdfContent,
        htmlContent,
        filename,
      })
      return response.data
    } catch (error) {
      console.error("Error sending scorecard email:", error)
      throw new Error(`Failed to send scorecard email: ${error}`)
    }
  }

  // Email with CC recipients
  async sendMailCC(to: string[], cc: string[], from: string, subject: string, message: string): Promise<EmailResponse> {
    try {
      const response = await axios.post(`${this.baseUrl}/sendMailCC`, {
        to,
        cc,
        from,
        subject,
        message,
      })
      return response.data
    } catch (error) {
      console.error("Error sending email with CC:", error)
      throw new Error(`Failed to send email with CC: ${error}`)
    }
  }

  async sendMailfeeback(to: string[], from: string, subject: string, message: string): Promise<EmailResponse> {
    try {
      const response = await axios.post(`${this.baseUrl}/sendMailfeeback`, {
        to,
        from,
        subject,
        message,
      })
      return response.data
    } catch (error) {
      console.error("Error sending feedback email:", error)
      throw new Error(`Failed to send feedback email: ${error}`)
    }
  }

  // Utility method to convert file to base64 for attachments
  static fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const base64 = reader.result as string
        // Remove data:mime/type;base64, prefix
        const base64Data = base64.split(",")[1]
        resolve(base64Data)
      }
      reader.onerror = (error) => reject(error)
    })
  }
}
